import type { LetterGrade, ScanGrade, ScanResult, Severity } from './types';

export type Tone = 'good' | 'warn' | 'bad';

export const GRADE_LABEL: Record<ScanGrade, string> = {
  good: 'Good',
  attention: 'Needs attention',
  risky: 'High risk',
};

export const SEVERITY_LABEL: Record<Severity, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
};

/** Bars of the "total findings" tile, highest severity first. */
const SEVERITY_ORDER: readonly Severity[] = ['high', 'medium', 'low', 'info'];

export const letterTone = (grade: LetterGrade): Tone =>
  grade === 'A' ? 'good' : grade === 'B' ? 'warn' : 'bad';

export const scoreTone = (score: number): Tone => (score >= 85 ? 'good' : score >= 65 ? 'warn' : 'bad');

// Chart box of the OWASP tile (viewBox 96x50): one point per group A01-A10, height = findings.
const CHART_W = 96;
const CHART_H = 50;
const X0 = 4;
const X_STEP = 88 / 9;
const Y_BASE = 44;
const Y_SPAN = 38;

export function buildOwaspChart(perCategory: readonly number[]) {
  const peak = Math.max(1, ...perCategory);
  const x = (i: number) => X0 + i * X_STEP;
  const y = (n: number) => Y_BASE - (n / peak) * Y_SPAN;
  const line = `M${perCategory.map((n, i) => `${x(i).toFixed(1)} ${y(n).toFixed(1)}`).join(' L')}`;
  const top = perCategory.reduce((best, n, i) => (n > perCategory[best] ? i : best), 0);

  return {
    viewBox: `0 0 ${CHART_W} ${CHART_H}`,
    line,
    area: `${line} L${x(perCategory.length - 1).toFixed(1)} ${Y_BASE} L${x(0).toFixed(1)} ${Y_BASE} Z`,
    grid: perCategory.map((_, i) => `M${x(i).toFixed(1)} 4V${Y_BASE}`).join(''),
    dot: { left: `${((x(top) / CHART_W) * 100).toFixed(2)}%`, top: `${((y(perCategory[top]) / CHART_H) * 100).toFixed(2)}%` },
    // A tall peak has no room above it for the label, so it hangs below the dot instead.
    tipBelow: y(perCategory[top]) < 18,
    peakLabel: `A${String(top + 1).padStart(2, '0')} · ${perCategory[top]}`,
    axis: perCategory.map((n, i) => ({ label: `A${String(i + 1).padStart(2, '0')}`, active: n > 0 })),
  };
}

export function buildResultView(result: ScanResult) {
  const { counts, findings, metrics } = result;
  const total = findings.length;
  const tallest = Math.max(1, ...SEVERITY_ORDER.map((s) => counts[s]));
  const actionable = counts.high + counts.medium + counts.low;
  const missing = metrics.headers.missing;

  return {
    total,
    bars: SEVERITY_ORDER.map((severity) => ({
      severity,
      count: counts[severity],
      height: `${Math.round((counts[severity] / tallest) * 100)}%`,
    })),
    verdict: `Score ${result.score}/100. ${actionable} issues to fix across ${result.totalChecks} checks.`,
    missingText: missing.length ? missing.join(', ') : 'All key headers present',
    owasp: buildOwaspChart(metrics.owasp.perCategory),
    lockedCount: Math.max(0, total - 1),
    ringOffset: 301.6 * (1 - result.score / 100),
  };
}
